import type {
  SpecPolicyFlagDomain,
  SpecPolicyFlagFamilyId,
  SpecPolicyFlagSilencePosture,
  SpecPolicyFlagScopeLevel,
  SpecPolicyFlagValue
} from "@automatry/schemas";
import {
  CENTRAL_SCREEN_TOPOLOGY_MODE_VALUES,
  SPEC_POLICY_FLAG_CATALOG_VERSION,
  SPEC_POLICY_FLAG_FAMILY_IDS
} from "@automatry/schemas";

export type SpecPolicyFlagValueKind = SpecPolicyFlagValue["kind"];

export interface SpecPolicyFlagCatalogFamily {
  readonly id: SpecPolicyFlagFamilyId;
  readonly order: number;
  readonly domain: SpecPolicyFlagDomain;
  readonly name: string;
  readonly scopeLevels: readonly SpecPolicyFlagScopeLevel[];
  readonly valueType: {
    readonly kind: SpecPolicyFlagValueKind;
    readonly values?: readonly string[];
    readonly objectType?: string;
    readonly description: string;
  };
  readonly plainMeaning: string;
  readonly defaultValue: SpecPolicyFlagValue;
  readonly defaultRule: string;
  readonly silencePosture: SpecPolicyFlagSilencePosture;
  readonly silencePostureRationale: string;
  readonly evidenceTriggers: readonly string[];
  readonly downstreamConsumer: string;
  readonly suppliesPhysicalUnit?: boolean;
  readonly roomTypeDefaults?: Record<
    string,
    { readonly min: number; readonly max: number }
  >;
  readonly scheduleBehavior?: string;
}

const booleanValue = (value: boolean): SpecPolicyFlagValue => ({
  kind: "boolean",
  value
});

const modeValue = (mode: string): SpecPolicyFlagValue => ({
  kind: "mode",
  mode
});

export const SPEC_POLICY_FLAG_ROOM_TYPE_LOOP_DEFAULTS = {
  living: { min: 2, max: 3 },
  lkd: { min: 2, max: 3 },
  kitchen: { min: 1, max: 2 },
  bedroom: { min: 1, max: 1 },
  hallway: { min: 1, max: 1 },
  bathroomWet: { min: 1, max: 1 },
  ensuiteWet: { min: 1, max: 1 }
} as const;

export const SPEC_POLICY_FLAG_CATALOG_V3 = [
  {
    id: "control-topology.central-screen-mode",
    order: 1,
    domain: "control-topology",
    name: "Central screen topology",
    scopeLevels: ["apartment-type", "project"],
    valueType: {
      kind: "mode",
      values: CENTRAL_SCREEN_TOPOLOGY_MODE_VALUES,
      description: "Whether the central screen is extra UI or replaces a local stat."
    },
    plainMeaning:
      "Controls whether a central apartment screen is added on top of local stats or acts as the stat for its own zone.",
    defaultValue: modeValue("central_screen_additive"),
    defaultRule:
      "Use additive only when no source evidence says the central screen substitutes for a local stat.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Additive is Rich's confirmed safe default on silence provided every application carries the visible no-trigger label; F1's selector-based extraction is expected to resolve most real cases before silence applies.",
    evidenceTriggers: [
      "central screen described as master or control stat",
      "central interface replaces or satisfies a local stat",
      "no separate stat or screen for the central-screen zone",
      "bedroom Touch-4, RDU, or room-stat rule separate from central screen",
      "bathroom, en-suite, or towel-rail ownership or slaving wording",
      "corridor or other-zone remote sensor wording"
    ],
    downstreamConsumer:
      "Thermostat-position classification and screen/sensor BOM before allocation."
  },
  {
    id: "sensor-policy.supply-air",
    order: 2,
    domain: "sensor-policy",
    name: "Supply air sensor policy",
    scopeLevels: ["apartment-type", "requirement", "project"],
    valueType: {
      kind: "mode",
      values: [
        "include_per_fcu",
        "exclude_unless_specified",
        "by_others",
        "needs_user_decision"
      ],
      description: "How supply-air/off-coil FCU sensors enter scope."
    },
    plainMeaning:
      "Controls whether each FCU receives a supply-air sensor, whether it is excluded, or whether another trade supplies it.",
    defaultValue: modeValue("include_per_fcu"),
    defaultRule:
      "Recommended standard default when tender is silent, visible with standard-default provenance.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Rich confirmed include_per_fcu as the standing higher-spec default on silence; a wrong guess here is a commercial sensor-count risk, not a wrong controller/topology commitment.",
    evidenceTriggers: [
      "supply air sensor",
      "off-coil sensor",
      "leaving air or discharge air",
      "FCU schedule requiring off-coil monitoring or control",
      "by others or provided by mechanical",
      "not in controls scope"
    ],
    downstreamConsumer: "Point ledger, sensor BOM, controller IO, quote, and export."
  },
  {
    id: "sensor-policy.return-air",
    order: 3,
    domain: "sensor-policy",
    name: "Return air sensor policy",
    scopeLevels: ["apartment-type", "requirement", "project"],
    valueType: {
      kind: "mode",
      values: [
        "include_per_fcu",
        "exclude_unless_specified",
        "by_others",
        "needs_user_decision"
      ],
      description: "How return-air FCU sensors enter scope."
    },
    plainMeaning:
      "Controls whether return-air sensors are added per FCU or withheld unless the tender explicitly asks for them.",
    defaultValue: modeValue("exclude_unless_specified"),
    defaultRule:
      "Rich-confirmed default is exclude unless specified; return-air sensors need source evidence or explicit default/override.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Rich locked exclude_unless_specified as the final 2026-07-05 default; it is a conservative absence-of-evidence choice, not a guess among equally plausible product outcomes.",
    evidenceTriggers: [
      "return air sensor",
      "room return air",
      "extract or return monitoring",
      "control sequence requiring return-air feedback",
      "explicit exclusion or by-others wording"
    ],
    downstreamConsumer: "Point ledger, sensor BOM, controller IO, quote, and export."
  },
  {
    id: "item-class-scope.scope",
    order: 4,
    domain: "item-class-scope",
    name: "Item class scope",
    scopeLevels: ["item-class", "project", "apartment-type"],
    valueType: {
      kind: "mode",
      values: [
        "include",
        "exclude_from_bom",
        "by_others",
        "commission_only",
        "install_only",
        "price_zero_with_note"
      ],
      description: "Whether a product/material/labour/commissioning class is in scope."
    },
    plainMeaning:
      "Controls whether a class of items enters the BOM, is by others, or is shown only as install/commission/zero-price scope.",
    defaultValue: modeValue("include"),
    defaultRule:
      "Include normal Titan controls material and controls-labour classes unless source evidence or a standard default says otherwise.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Include is the safe absence-of-evidence reading for ordinary item classes; getting it wrong is a commercial over-inclusion risk, not a silent controller/topology misconfiguration.",
    evidenceTriggers: [
      "by others or supplied by others",
      "free issue",
      "excluded or not included",
      "commissioning only",
      "install only",
      "client supplied",
      "scope matrix assigns material, labour, or commissioning",
      "tender alternate excludes a family or item class"
    ],
    downstreamConsumer:
      "Load-bearing item classes enter allocation; output overlays dim, zero-price, or exclude display/export rows.",
    scheduleBehavior:
      "Excluded or by-others outcomes create exclusion schedule entries in later chains."
  },
  {
    id: "netmaster-capability.remote-app-control",
    order: 5,
    domain: "netmaster-capability",
    name: "Remote app control",
    scopeLevels: ["project", "apartment-type"],
    valueType: {
      kind: "boolean",
      description:
        "True when remote app, mobile, cloud, or smart-home control is required."
    },
    plainMeaning: "A NetMaster capability trigger for remote/mobile/cloud app control.",
    defaultValue: booleanValue(false),
    defaultRule: "False when tender is silent.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Absence of remote/mobile/cloud-control wording reliably means the capability is not required; false is the direct entailment of silence, not a guess.",
    evidenceTriggers: [
      "remote app control",
      "mobile app",
      "cloud control",
      "smart-home control",
      "remote apartment HVAC control"
    ],
    downstreamConsumer: "Derived NetMaster required posture and infrastructure BOM."
  },
  {
    id: "netmaster-capability.modbus",
    order: 6,
    domain: "netmaster-capability",
    name: "Modbus or BMS gateway",
    scopeLevels: ["project", "apartment-type"],
    valueType: {
      kind: "boolean",
      description: "True when protocol exposure or BMS gateway capability is required."
    },
    plainMeaning:
      "A NetMaster capability trigger for Modbus, BACnet, BMS, metering dashboard, or protocol exposure.",
    defaultValue: booleanValue(false),
    defaultRule: "False when tender is silent.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Absence of Modbus/BACnet/BMS/gateway wording reliably means the capability is not required; false is the direct entailment of silence, not a guess.",
    evidenceTriggers: [
      "MODBUS",
      "BACnet",
      "BMS gateway",
      "metering dashboard",
      "protocol exposure",
      "third-party integration",
      "CCTV, fire-alarm, or smoke-vent interface"
    ],
    downstreamConsumer: "Derived NetMaster required posture and network topology."
  },
  {
    id: "netmaster-capability.time-scheduling",
    order: 7,
    domain: "netmaster-capability",
    name: "Time scheduling",
    scopeLevels: ["project", "apartment-type"],
    valueType: {
      kind: "boolean",
      description:
        "True when time scheduling or time clocks require NetMaster capability."
    },
    plainMeaning:
      "A NetMaster capability trigger for time schedules, time clocks, boosts, or timed control.",
    defaultValue: booleanValue(false),
    defaultRule: "False when tender is silent.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Absence of time-scheduling/time-clock wording reliably means the capability is not required; false is the direct entailment of silence, not a guess.",
    evidenceTriggers: ["time scheduling", "time clock", "timed control", "boost"],
    downstreamConsumer: "Derived NetMaster required posture and infrastructure BOM."
  },
  {
    id: "netmaster-capability.holiday-mode",
    order: 8,
    domain: "netmaster-capability",
    name: "Holiday mode",
    scopeLevels: ["project", "apartment-type"],
    valueType: {
      kind: "boolean",
      description: "True when holiday-mode behavior requires NetMaster capability."
    },
    plainMeaning:
      "A NetMaster capability trigger for holiday mode or away-mode apartment control.",
    defaultValue: booleanValue(false),
    defaultRule: "False when tender is silent.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Absence of holiday/away-mode wording reliably means the capability is not required; false is the direct entailment of silence, not a guess.",
    evidenceTriggers: ["holiday mode", "away mode", "vacation mode"],
    downstreamConsumer: "Derived NetMaster required posture and infrastructure BOM."
  },
  {
    id: "netmaster-capability.central-control",
    order: 9,
    domain: "netmaster-capability",
    name: "Central control",
    scopeLevels: ["project", "apartment-type"],
    valueType: {
      kind: "boolean",
      description: "True when centralised control requires NetMaster capability."
    },
    plainMeaning:
      "A NetMaster capability trigger for whole-apartment, building, landlord BMS, or FM central control.",
    defaultValue: booleanValue(false),
    defaultRule: "False when tender is silent.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Absence of centralised/BMS/FM control wording reliably means the capability is not required; false is the direct entailment of silence, not a guess.",
    evidenceTriggers: [
      "centralised setpoint",
      "central control",
      "whole-apartment master control",
      "landlord BMS",
      "building BMS",
      "estate BMS",
      "FM interface"
    ],
    downstreamConsumer:
      "Derived NetMaster required posture and controller/network topology."
  },
  {
    id: "netmaster-capability.io-module-mapping",
    order: 10,
    domain: "netmaster-capability",
    name: "IO module mapping",
    scopeLevels: ["project", "apartment-type"],
    valueType: {
      kind: "boolean",
      description:
        "True when IO-module mapping or handover requires NetMaster capability."
    },
    plainMeaning:
      "A NetMaster capability trigger for IO module mapping, network management, commissioning, or handover requirements.",
    defaultValue: booleanValue(false),
    defaultRule: "False when tender is silent.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Absence of IO-mapping/handover wording reliably means the capability is not required; false is the direct entailment of silence, not a guess.",
    evidenceTriggers: [
      "IO-module mapping",
      "I/O mapping",
      "network management",
      "commissioning handover",
      "handover requiring network management"
    ],
    downstreamConsumer:
      "Derived NetMaster required posture and mapping/commissioning outputs."
  },
  {
    id: "netmaster-capability.central-panel-drives-actuators",
    order: 11,
    domain: "netmaster-capability",
    name: "Central panel drives actuators",
    scopeLevels: ["project", "apartment-type"],
    valueType: {
      kind: "boolean",
      description:
        "True when central panel relays or actuators require NetMaster capability."
    },
    plainMeaning:
      "A NetMaster capability trigger when a central panel drives actuators or central relays.",
    defaultValue: booleanValue(false),
    defaultRule: "False when tender is silent.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Absence of central-panel-driven-actuator wording reliably means the capability is not required; false is the direct entailment of silence, not a guess.",
    evidenceTriggers: [
      "central panel driving actuators",
      "central relays",
      "panel relay outputs",
      "central panel actuator outputs"
    ],
    downstreamConsumer: "Derived NetMaster required posture, panel topology, and BOM."
  },
  {
    id: "network-power.poe-switch-inclusion",
    order: 12,
    domain: "network-power",
    name: "PoE switch inclusion",
    scopeLevels: ["project", "apartment-type"],
    valueType: {
      kind: "mode",
      values: [
        "include_when_screens_present",
        "explicit_by_others",
        "not_required",
        "needs_user_decision"
      ],
      description:
        "Whether PoE switch/network support is included, excluded, or by others."
    },
    plainMeaning:
      "Controls PoE switch inclusion for networked screens and central interfaces.",
    defaultValue: modeValue("include_when_screens_present"),
    defaultRule:
      "Include when networked or central screens are present; use not_required when no PoE/networked screens exist.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "The default follows deterministically from already-resolved screen/interface evidence, not an independent guess about an unresolved product fork.",
    evidenceTriggers: [
      "touch screens",
      "PoE screens",
      "central interfaces",
      "networked room controllers",
      "PoE switch by others",
      "network switch by others",
      "provided by IT"
    ],
    downstreamConsumer:
      "Network infrastructure, panel/enclosure support BOM, quote, and export.",
    scheduleBehavior:
      "not_required or explicit_by_others outcomes create exclusion schedule entries in later chains."
  },
  {
    id: "ufh-policy.loop-count-default",
    order: 13,
    domain: "ufh-policy",
    name: "UFH loop-count default",
    scopeLevels: ["apartment-type", "room"],
    valueType: {
      kind: "structured",
      objectType: "UfhLoopCountDefault",
      description:
        "Small/large UFH loop default plus trigger source; room-type defaults live in catalog metadata."
    },
    plainMeaning:
      "Controls visible UFH loop-count assumptions when the tender has no manifold or loop schedule.",
    defaultValue: {
      kind: "structured",
      objectType: "UfhLoopCountDefault",
      smallAreaLoops: 1,
      largeAreaMin: 2,
      largeAreaMax: 3,
      largeAreaTrigger: "room_kind_standard"
    },
    defaultRule:
      "Apply room-type table when source is silent; standard-default, low confidence, editable per room.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Rich pinned a concrete per-room-type loop table (2026-07-05) as the required visible low-confidence default; it is a quantity assumption editable per room, not a hard topology fork.",
    evidenceTriggers: [
      "UFH manifold schedule",
      "loop schedule",
      "bathroom heating layout",
      "large bathroom",
      "master ensuite",
      "wet area",
      "explicit loop count"
    ],
    downstreamConsumer:
      "UFH point demands, actuator assumptions, sensor/limit policy, BOM, and parity scoring.",
    roomTypeDefaults: SPEC_POLICY_FLAG_ROOM_TYPE_LOOP_DEFAULTS
  },
  {
    id: "product-generation.era",
    order: 14,
    domain: "product-generation",
    name: "Product generation era",
    scopeLevels: ["project", "apartment-type"],
    valueType: {
      kind: "mode",
      values: ["current_catalog", "legacy_fcu_503_refit"],
      description:
        "Whether current Titan products or legacy FCU-503 refit products apply."
    },
    plainMeaning:
      "Controls current-catalog versus legacy FCU-503/MZ refit product selection.",
    defaultValue: modeValue("current_catalog"),
    defaultRule:
      "New tenders generate current-catalog products unless source-backed retrofit evidence exists.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Rich narrowed legacy evidence to explicit user/email/scope-spec retrofit references only; silence unambiguously means current_catalog by directive, not a derivation problem needing mechanical research.",
    evidenceTriggers: [
      "FCU-503",
      "MZ",
      "FCU-503-CYL",
      "legacy wording",
      "refit or retrofit wording",
      "existing-controller reuse",
      "like-for-like replacement",
      "user note, client email, estimator email, or explicit scope/spec retrofit reference"
    ],
    downstreamConsumer:
      "Controller selection, parity mapping, catalog lookup, quote, and export labels."
  },
  {
    id: "control-topology.local-interface-product",
    order: 15,
    domain: "control-topology",
    name: "Local interface product",
    scopeLevels: ["apartment-type", "room"],
    valueType: {
      kind: "mode",
      values: ["ts4", "ts7", "sensor_only", "rdu", "none"],
      description: "Which local interface product ships where F1 has not removed it."
    },
    plainMeaning:
      "Controls the local room interface product for zones not already covered by central-screen substitution.",
    defaultValue: modeValue("ts4"),
    defaultRule:
      "Default to TS-4 only for zones not classified as central-screen stat or slaved by F1.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "TS-4 is Rich's pinned baseline local-interface product for zones F1 has not already reclassified; it is the expected default product, not an ambiguous guess.",
    evidenceTriggers: [
      "thermostat",
      "touchscreen",
      "4-inch or four inch",
      "7-inch or seven inch",
      "RDU as a literal product",
      "local display",
      "room control",
      "sensor only",
      "no display"
    ],
    downstreamConsumer:
      "Thermostat-position product selection before screen/sensor BOM."
  },
  {
    id: "ufh-policy.zone-type",
    order: 16,
    domain: "ufh-policy",
    name: "UFH zone type",
    scopeLevels: ["apartment-type", "room"],
    valueType: {
      kind: "structured",
      objectType: "UfhZoneType",
      description:
        "Overall wet/electric/mixed/none UFH type with optional bathroom override."
    },
    plainMeaning:
      "Controls whether UFH is wet, electric, mixed, absent, and whether bathrooms differ.",
    defaultValue: {
      kind: "structured",
      objectType: "UfhZoneType",
      overall: "needs_user_decision"
    },
    defaultRule:
      "Tender silence is needs_user_decision; concrete wet/electric/mixed/none values require UFH evidence or an explicit user decision.",
    silencePosture: "derivable-requires-evidence",
    silencePostureRationale:
      "Wet versus electric UFH forks the entire downstream actuator/manifold-versus-relay topology exactly like fcu-mode forks controller selection; no concrete zone type may apply without evidence-backed extraction or a user decision.",
    evidenceTriggers: [
      "wet UFH",
      "hydronic UFH",
      "LTHW underfloor",
      "electric UFH",
      "cable UFH",
      "mat UFH",
      "resistance heating",
      "screed sensor",
      "floor sensor",
      "slab sensor",
      "floor probe",
      "bathroom-specific UFH wording"
    ],
    downstreamConsumer:
      "Wet actuator/manifold versus electric relay treatment before loop defaults."
  },
  {
    id: "ufh-policy.limit-sensor-redundancy",
    order: 17,
    domain: "ufh-policy",
    name: "UFH limit-sensor redundancy",
    scopeLevels: ["apartment-type", "project"],
    valueType: {
      kind: "boolean",
      description: "True means each UFH loop gets active plus spare limit sensors."
    },
    plainMeaning: "Controls whether UFH limit sensors are doubled for redundancy.",
    defaultValue: booleanValue(false),
    defaultRule:
      "True only with explicit redundancy evidence; otherwise visible low-confidence standard default remains editable.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Absence of redundancy wording reliably means single (non-redundant) sensors; false is the direct entailment of silence and only adds IO when explicit evidence appears.",
    evidenceTriggers: [
      "2nr sensors per loop",
      "one active one spare",
      "redundant limit sensor",
      "backup sensor",
      "dual sensor",
      "twin sensors"
    ],
    downstreamConsumer: "UFH limit-sensor point and IO count."
  },
  {
    id: "network-power.central-panel-enclosure-inclusion",
    order: 18,
    domain: "network-power",
    name: "Central panel enclosure inclusion",
    scopeLevels: ["project", "apartment-type"],
    valueType: {
      kind: "mode",
      values: ["include", "explicit_by_others", "not_required", "needs_user_decision"],
      description:
        "Whether a central panel/enclosure is included, by others, or not required."
    },
    plainMeaning: "Controls central control panel or enclosure BOM inclusion.",
    defaultValue: modeValue("not_required"),
    defaultRule:
      "Include when NetMaster capability or central-screen/panel evidence requires it; not_required otherwise.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "The default follows deterministically from already-resolved NetMaster/central-screen evidence, not an independent guess about an unresolved product fork.",
    evidenceTriggers: [
      "central control panel",
      "control enclosure",
      "panel enclosure",
      "metal cabinet",
      "DIN rail panel",
      "wiring centre",
      "wiring center",
      "panel supply by others"
    ],
    downstreamConsumer: "Enclosure/panel BOM row and network infrastructure support.",
    scheduleBehavior:
      "not_required or explicit_by_others outcomes create exclusion schedule entries in later chains."
  },
  {
    id: "network-power.metering-integration",
    order: 19,
    domain: "network-power",
    name: "Metering integration",
    scopeLevels: ["project", "apartment-type"],
    valueType: {
      kind: "structured",
      objectType: "MeteringIntegration",
      description: "Included boolean plus metering protocol."
    },
    plainMeaning:
      "Controls whether physical metering hardware or IO exists and which protocol applies.",
    defaultValue: {
      kind: "structured",
      objectType: "MeteringIntegration",
      included: false,
      protocol: "none"
    },
    defaultRule: "Default to no metering integration when tender is silent.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Absence of metering wording reliably means no metering integration is required; the false/none default directly follows from silence.",
    evidenceTriggers: [
      "M-Bus",
      "heat meter",
      "energy meter",
      "sub-meter",
      "HIU metering",
      "kWh meter",
      "energy monitoring",
      "BACnet",
      "Modbus",
      "pulse meter",
      "wireless M-Bus"
    ],
    downstreamConsumer:
      "Metering IO/point reservation and controller M-Bus port allocation."
  },
  {
    id: "sensor-policy.district-interface-units",
    order: 20,
    domain: "sensor-policy",
    name: "District interface units",
    scopeLevels: ["apartment-type", "project"],
    valueType: {
      kind: "structured",
      objectType: "DistrictInterfaceUnits",
      description: "HIU and CIU presence booleans."
    },
    plainMeaning:
      "Controls whether HIU and/or CIU related sensor and metering points are expected.",
    defaultValue: {
      kind: "structured",
      objectType: "DistrictInterfaceUnits",
      hiu: false,
      ciu: false
    },
    defaultRule: "Default both HIU and CIU to false when tender is silent.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Absence of HIU/CIU wording reliably means no district interface sensor points are required; the false/false default directly follows from silence, independent of the interface-demand take-off-driven families.",
    evidenceTriggers: [
      "HIU",
      "heat interface unit",
      "district heating interface",
      "heat substation",
      "hydraulic interface",
      "CIU",
      "cooling interface unit",
      "chilled water interface",
      "district cooling interface"
    ],
    downstreamConsumer: "HIU/CIU sensor, metering point, and interface allocation."
  },
  {
    id: "controller-policy.fcu-mode",
    order: 21,
    domain: "controller-policy",
    name: "FCU mode",
    scopeLevels: ["apartment-type", "project"],
    valueType: {
      kind: "mode",
      values: [
        "heating_cooling",
        "cool_only",
        "heat_only",
        "needs_user_decision"
      ],
      description:
        "Whether FCUs need heating/cooling, cooling-only, heat-only, or an explicit user decision before controller selection."
    },
    plainMeaning:
      "Controls the FCU heating/cooling mode used for controller product selection.",
    defaultValue: modeValue("needs_user_decision"),
    defaultRule:
      "Tender silence is needs_user_decision, not Proposal Forge's heating_cooling. Any later concrete default applies only when no specific requirement exists and must be labelled default — no trigger found.",
    silencePosture: "derivable-requires-evidence",
    silencePostureRationale:
      "Rich's Q16 answer is explicit: FCU heating/cooling mode must be derived from mechanical evidence (pipe count, equipment schedules) via a dedicated research pass, never guessed on silence; the only silence-safe value is needs_user_decision.",
    evidenceTriggers: [
      "FCU",
      "fan coil",
      "heating/cooling",
      "chilled water",
      "LTHW",
      "cooling-only or no-heating language",
      "heating-only or no-cooling language"
    ],
    downstreamConsumer: "Controller product selection and parity mapping."
  },
  {
    id: "supply-scope.ufh-actuator-supply",
    order: 22,
    domain: "supply-scope",
    name: "UFH actuator supply",
    scopeLevels: ["project", "apartment-type"],
    valueType: {
      kind: "boolean",
      description: "True means Titan/controls supplies and prices wet-UFH actuators."
    },
    plainMeaning:
      "Controls whether wet-UFH zone or loop actuators are supplied by Titan/controls or by others.",
    defaultValue: booleanValue(false),
    defaultRule:
      "Default OFF: supplied by others unless explicit scope says controls supplies.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Rich confirmed default OFF (supplied by others) is the correct standing behaviour, not a guess; ON only applies with explicit scope/spec supply evidence.",
    evidenceTriggers: [
      "controls to supply UFH actuators",
      "BMS scope includes UFH actuator supply",
      "UFH actuators by controls contractor"
    ],
    downstreamConsumer: "BOM/product inclusion and exclusion schedule generation.",
    scheduleBehavior: "False creates an exclusion schedule entry in later chains."
  },
  {
    id: "supply-scope.fcu-actuator-supply",
    order: 23,
    domain: "supply-scope",
    name: "FCU actuator supply",
    scopeLevels: ["project", "apartment-type"],
    valueType: {
      kind: "boolean",
      description: "True means Titan/controls supplies and prices FCU valve actuators."
    },
    plainMeaning:
      "Controls whether FCU 2-port or 3-port valve actuators are supplied by Titan/controls or by others.",
    defaultValue: booleanValue(false),
    defaultRule:
      "Default OFF: supplied by others unless explicit scope says controls supplies.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Rich confirmed default OFF (supplied by others, normally M&E) is the correct standing behaviour, not a guess; ON only applies with explicit scope/spec supply evidence.",
    evidenceTriggers: [
      "controls to supply FCU actuators",
      "BMS scope includes FCU actuator supply",
      "FCU valve actuators by controls contractor"
    ],
    downstreamConsumer: "BOM/product inclusion and exclusion schedule generation.",
    scheduleBehavior: "False creates an exclusion schedule entry in later chains."
  },
  {
    id: "supply-scope.riser-cabling-supply",
    order: 24,
    domain: "supply-scope",
    name: "Riser cabling supply",
    scopeLevels: ["project", "apartment-type"],
    valueType: {
      kind: "boolean",
      description:
        "True means Titan/controls supplies CAT6-tested riser cabling install/support rows."
    },
    plainMeaning:
      "Controls whether CAT6-tested riser cabling is in the apartment-controls quote or excluded/by others.",
    defaultValue: booleanValue(false),
    defaultRule:
      "Default OFF: riser cabling is excluded unless explicit controls scope covers it.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Rich confirmed default OFF (excluded from the apartment-controls quote) is the correct standing behaviour, not a guess; ON only applies with explicit controls-scope evidence.",
    evidenceTriggers: [
      "riser cabling by controls contractor",
      "CAT6 riser install within BMS scope",
      "controls to provide riser cabling",
      "BMS scope includes riser cabling"
    ],
    downstreamConsumer: "BOM/product inclusion and exclusion schedule generation.",
    scheduleBehavior: "False creates an exclusion schedule entry in later chains."
  },
  {
    id: "interface-demand.hiu-interface",
    order: 25,
    domain: "interface-demand",
    name: "HIU interface demand",
    scopeLevels: ["apartment-type", "room"],
    valueType: {
      kind: "boolean",
      description:
        "True means take-off-derived HIUs drive heating-demand interface points."
    },
    plainMeaning:
      "HIUs are not physically supplied by Titan, but detected HIUs drive interface/point specification.",
    defaultValue: booleanValue(true),
    defaultRule:
      "Default true: take-off-derived HIU counts drive interface requirements.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Default true is a structural policy Rich confirmed (2026-07-05): take-off-derived HIU counts must always drive interface points regardless of physical supply; it is not an ambiguous product guess.",
    evidenceTriggers: ["HIU", "heat interface unit", "derived_devices.HIU"],
    downstreamConsumer: "Point ledger and interface-point specification.",
    suppliesPhysicalUnit: false
  },
  {
    id: "interface-demand.ciu-interface",
    order: 26,
    domain: "interface-demand",
    name: "CIU interface demand",
    scopeLevels: ["apartment-type", "room"],
    valueType: {
      kind: "boolean",
      description:
        "True means take-off-derived CIUs drive cooling-demand interface points."
    },
    plainMeaning:
      "CIUs are not physically supplied by Titan, but detected CIUs drive interface/point specification.",
    defaultValue: booleanValue(true),
    defaultRule:
      "Default true: take-off-derived CIU counts drive interface requirements.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Default true is a structural policy Rich confirmed (2026-07-05): take-off-derived CIU counts must always drive interface points regardless of physical supply; it is not an ambiguous product guess.",
    evidenceTriggers: ["CIU", "cooling interface unit", "derived_devices.CIU"],
    downstreamConsumer: "Point ledger and interface-point specification.",
    suppliesPhysicalUnit: false
  },
  {
    id: "interface-demand.electric-ufh-zone-interface",
    order: 27,
    domain: "interface-demand",
    name: "Electric UFH zone interface demand",
    scopeLevels: ["apartment-type", "room"],
    valueType: {
      kind: "boolean",
      description:
        "True means take-off-derived electric UFH zones drive interface points."
    },
    plainMeaning:
      "Electric UFH zones are not necessarily supplied as physical units by Titan, but they drive control/interface points.",
    defaultValue: booleanValue(true),
    defaultRule:
      "Default true: take-off-derived electric UFH zones drive interface requirements.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Default true is a structural policy Rich confirmed (2026-07-05): take-off-derived electric-UFH-zone counts must always drive interface points; it is not an ambiguous product guess.",
    evidenceTriggers: [
      "EUFH",
      "electric UFH zone",
      "electric underfloor heating",
      "derived_devices.EUFH"
    ],
    downstreamConsumer: "Point ledger and interface-point specification.",
    suppliesPhysicalUnit: false
  },
  {
    id: "interface-demand.towel-rail-interface",
    order: 28,
    domain: "interface-demand",
    name: "Towel rail interface demand",
    scopeLevels: ["apartment-type", "room"],
    valueType: {
      kind: "boolean",
      description:
        "True means take-off-derived towel rails drive control output points."
    },
    plainMeaning:
      "Towel rails are not physically supplied by Titan, but detected towel rails drive control output points.",
    defaultValue: booleanValue(true),
    defaultRule:
      "Default true: take-off-derived towel-rail counts drive interface requirements.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Default true is a structural policy Rich confirmed (2026-07-05): take-off-derived towel-rail counts must always drive interface/output points; it is not an ambiguous product guess.",
    evidenceTriggers: [
      "towel rail",
      "towel rail output",
      "heated towel rail",
      "derived_devices.TOWEL RAIL"
    ],
    downstreamConsumer: "Point ledger and interface-point specification.",
    suppliesPhysicalUnit: false
  },
  {
    id: "interface-demand.wet-ufh-actuator-interface",
    order: 29,
    domain: "interface-demand",
    name: "Wet UFH actuator interface demand",
    scopeLevels: ["apartment-type", "room"],
    valueType: {
      kind: "boolean",
      description:
        "True means take-off-derived wet UFH actuators drive control/interface points."
    },
    plainMeaning:
      "Wet-UFH actuators may be supplied by others, but detected actuator demand still drives interface points.",
    defaultValue: booleanValue(true),
    defaultRule:
      "Default true: take-off-derived wet-UFH actuator counts drive interface requirements.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "Default true is a structural policy Rich confirmed (2026-07-05): take-off-derived wet-UFH-actuator counts must always drive interface points regardless of supply; it is not an ambiguous product guess.",
    evidenceTriggers: [
      "wet UFH actuator",
      "UFH actuator",
      "wet actuator",
      "derived_devices.WET ACTUATOR"
    ],
    downstreamConsumer: "Point ledger and interface-point specification.",
    suppliesPhysicalUnit: false
  },
  {
    id: "netmaster-capability.no-trigger-posture",
    order: 30,
    domain: "netmaster-capability",
    name: "No-trigger NetMaster advisory",
    scopeLevels: ["project"],
    valueType: {
      kind: "mode",
      values: ["advisory_lean_include", "advisory_lean_exclude", "needs_user_decision"],
      description: "Advisory posture when no hard NetMaster capability trigger fires."
    },
    plainMeaning:
      "Surfaces a lean-yes or lean-exclude NetMaster advisory when no hard trigger requires NetMaster.",
    defaultValue: modeValue("advisory_lean_include"),
    defaultRule:
      "Default lean include as a low-confidence, overridable advisory only when hard NetMaster required derives false.",
    silencePosture: "labelled-default-ok",
    silencePostureRationale:
      "The family is itself an advisory posture, not a BOM commitment: Rich's lean-yes-but-cost-sensitive direction (2026-07-05) is surfaced as an overridable recommendation and never auto-adds NetMaster, so a labelled default carries no silent product risk.",
    evidenceTriggers: [
      "no hard trigger fired",
      "value engineering",
      "keep costs down",
      "budget constrained",
      "small simple bare-FCU-only scope",
      "sibling apartment types already carry NetMaster"
    ],
    downstreamConsumer:
      "NetMaster BOM-inclusion decision after the advisory is accepted; never automatic by itself.",
    scheduleBehavior:
      "advisory_lean_exclude creates an exclusion schedule entry in later chains; accepted advisory_lean_include may create an assumption entry."
  }
] as const satisfies readonly SpecPolicyFlagCatalogFamily[];

export const SPEC_POLICY_FLAG_CATALOG_ORDER = SPEC_POLICY_FLAG_CATALOG_V3.map(
  (family) => family.id
);

const specPolicyFlagCatalogById = {} as Record<
  SpecPolicyFlagFamilyId,
  SpecPolicyFlagCatalogFamily
>;

for (const family of SPEC_POLICY_FLAG_CATALOG_V3) {
  specPolicyFlagCatalogById[family.id] = family;
}

export const SPEC_POLICY_FLAG_CATALOG_BY_ID = specPolicyFlagCatalogById;

export const SPEC_POLICY_FLAG_CATALOG_METADATA = {
  version: SPEC_POLICY_FLAG_CATALOG_VERSION,
  schemaVersion: "spec-policy-flags-schema-v3",
  familyCount: SPEC_POLICY_FLAG_CATALOG_V3.length,
  familyIds: SPEC_POLICY_FLAG_CATALOG_ORDER,
  migrationPolicy:
    "Catalog growth is additive; old accepted specs keep recorded active vectors until explicit re-specify."
} as const;

export const assertSpecPolicyFlagCatalogCompleteness = (): void => {
  const ids = new Set(SPEC_POLICY_FLAG_CATALOG_ORDER);

  if (SPEC_POLICY_FLAG_CATALOG_ORDER.length !== SPEC_POLICY_FLAG_FAMILY_IDS.length) {
    throw new Error(
      `Expected ${SPEC_POLICY_FLAG_FAMILY_IDS.length} policy flag families, got ${SPEC_POLICY_FLAG_CATALOG_ORDER.length}.`
    );
  }

  for (const id of SPEC_POLICY_FLAG_FAMILY_IDS) {
    if (!ids.has(id)) {
      throw new Error(`Missing policy flag catalog family: ${id}`);
    }
  }
};
